import {
  CHANNEL_MESSAGE_CREATED,
  CHANNEL_MESSAGE_DELETED,
  CHANNEL_MESSAGE_UPDATED,
} from '@common/constants/webSockets'
import { IChannelMessage } from '@common/interfaces/channels'
import { Action } from 'redux'
import type { ISendChannelMessageParams } from '../../api/channels'
import { SOCKET_STATUS_CHANGED, SocketStatus } from '../connection/actions'
import * as actions from './actions'
import type {
  IChannelDetail,
  IChannelMessageView,
  IChannelsState,
  IChannelTimeline,
} from './reducer'

/**
 * Offline and instant-open behaviour for AIM, layered over the core channels reducer: it sees
 * the state before an action, the core reducer's result, and the action, and adjusts. Keeps
 * the outbox (unsent messages, persisted per user) and an LRU of recently opened timelines.
 */

export type OutboxStatus = 'sending' | 'queued' | 'failed'

export type IOutboxSendData = Omit<ISendChannelMessageParams, 'pageUrl' | 'clientMessageId'>

export interface IOutboxEntry {
  clientMessageId: string
  channelId: string
  data: IOutboxSendData
  createdAt: string
  status: OutboxStatus
  failedReason?: string
}

export interface ICachedTimeline {
  items: IChannelMessageView[]
  hasMore: boolean
  nextCursor: string | null
  nextCursorId: string | null
  cachedAt: number
}

export const MESSAGE_CACHE_LIMIT = 8

/** Older than this, a cached timeline is refreshed by replacement rather than merge — see Messages. */
export const CACHE_MERGE_WINDOW_MS = 5 * 60 * 1000

type OfflineAction = Action & {
  data?: unknown
  params?: Record<string, unknown>
  error?: Error & { status?: number }
}

/**
 * Still only the client's optimistic copy — in flight, queued or failed. Its id is a
 * `pending-…` temp id the server has never stored, so nothing that names the message
 * to the server can act on it yet.
 */
export const isUnsentMessage = (item: Pick<IChannelMessageView, 'isPending' | 'isFailed'>) =>
  !!item.isPending || !!item.isFailed

/**
 * How current the open timeline is. If the socket is connected now and was already connected
 * when the newest page arrived, live events have kept it up to date since. Anything else — not
 * connected yet, or reconnected since — leaves it current only as of that page.
 */
export const openTimelineFreshAt = (state: IChannelsState): number => {
  const syncedAt = state.messages.cachedAt ?? 0
  const connectedSince = state.socketConnectedSince
  return connectedSince !== null && connectedSince <= syncedAt ? Date.now() : syncedAt
}

/** The row an outbox entry renders as, rebuilt whenever its timeline is (re)loaded. */
export const rowFromOutboxEntry = (
  state: IChannelsState,
  entry: IOutboxEntry,
): IChannelMessageView => ({
  id: actions.tempIdForClientMessage(entry.clientMessageId),
  clientMessageId: entry.clientMessageId,
  channelId: entry.channelId,
  senderUserId: state.currentUserId ?? '',
  kind: 'message' as IChannelMessage['kind'],
  contentState: entry.data.contentState ?? null,
  content: entry.data.content ?? '',
  mentions: [],
  arcSessionId: null,
  arcTurnId: null,
  pinnedAt: null,
  pinnedByUserId: null,
  editedAt: null,
  createdAt: entry.createdAt,
  updatedAt: entry.createdAt,
  senderName: state.currentUserName ?? undefined,
  senderShortName: state.currentUserShortName ?? undefined,
  senderAvatar: state.currentUserAvatar,
  isPending: entry.status !== 'failed',
  isQueued: entry.status === 'queued',
  isFailed: entry.status === 'failed',
  failedReason: entry.failedReason,
  unsentData: entry.data,
})

const omitKeys = <T>(record: Record<string, T>, keys: Iterable<string>): Record<string, T> => {
  const toDrop = new Set(keys)
  if (![...toDrop].some((key) => key in record)) return record
  return Object.fromEntries(Object.entries(record).filter(([key]) => !toDrop.has(key)))
}

/**
 * A timeline with the outbox folded in: rows the server now holds (matched by clientMessageId)
 * replace their optimistic copies, and every unsent entry for the channel is on screen once.
 * Returns the outbox too, minus entries the server turned out to have.
 */
export const reconcileWithOutbox = (
  state: IChannelsState,
  channelId: string,
  items: IChannelMessageView[],
): { items: IChannelMessageView[]; outbox: Record<string, IOutboxEntry> } => {
  const storedClientIds = new Set(
    items
      .filter((item) => !isUnsentMessage(item) && item.clientMessageId)
      .map((i) => i.clientMessageId),
  )
  const outbox = omitKeys(state.outbox, storedClientIds)
  const kept = items.filter(
    (item) =>
      !(isUnsentMessage(item) && item.clientMessageId && storedClientIds.has(item.clientMessageId)),
  )
  const shown = new Set(kept.map((item) => item.id))
  const missing = Object.values(outbox)
    .filter((entry) => entry.channelId === channelId)
    .filter((entry) => !shown.has(actions.tempIdForClientMessage(entry.clientMessageId)))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((entry) => rowFromOutboxEntry(state, entry))
  return { items: missing.length ? [...kept, ...missing] : kept, outbox }
}

const upsertItem = (items: IChannelMessageView[], message: IChannelMessageView) => {
  const index = items.findIndex((item) => item.id === message.id)
  if (index === -1) return [...items, message]
  const next = items.slice()
  next[index] = { ...next[index], ...message }
  return next
}

/** Drops rows filed under another conversation's timeline, so a mixed copy is never stored or restored. */
export const withOwnRowsOnly = (
  cache: Record<string, ICachedTimeline>,
): Record<string, ICachedTimeline> => {
  const isMixed = Object.entries(cache).some(([channelId, timeline]) =>
    timeline.items.some((item) => item.channelId !== channelId),
  )
  if (!isMixed) return cache
  return Object.fromEntries(
    Object.entries(cache).map(([channelId, timeline]) => [
      channelId,
      { ...timeline, items: timeline.items.filter((item) => item.channelId === channelId) },
    ]),
  )
}

/** Put a timeline at the front of the LRU, evicting what falls off the end. */
const touchCache = (
  state: IChannelsState,
  channelId: string,
  entry: ICachedTimeline,
): Pick<IChannelsState, 'messageCache' | 'messageCacheOrder' | 'channelDetailCache'> => {
  const order = [channelId, ...state.messageCacheOrder.filter((id) => id !== channelId)]
  const evicted = order.slice(MESSAGE_CACHE_LIMIT)
  return {
    messageCacheOrder: order.slice(0, MESSAGE_CACHE_LIMIT),
    messageCache: omitKeys({ ...state.messageCache, [channelId]: entry }, evicted),
    channelDetailCache: omitKeys(state.channelDetailCache, evicted),
  }
}

const patchCachedTimeline = (
  state: IChannelsState,
  channelId: string | undefined,
  patch: (items: IChannelMessageView[]) => IChannelMessageView[],
): IChannelsState => {
  const cached = channelId ? state.messageCache[channelId] : undefined
  if (!channelId || !cached || channelId === state.messages.channelId) return state
  return {
    ...state,
    messageCache: { ...state.messageCache, [channelId]: { ...cached, items: patch(cached.items) } },
  }
}

/** Stash the timeline being left, then open the next one from the cache if it is there. */
const switchTimeline = (prev: IChannelsState, next: IChannelsState): IChannelsState => {
  let state = next
  const left = prev.messages
  if (left.channelId && !left.hasNewer && left.items.length) {
    const saved = left.items.filter((item) => !isUnsentMessage(item))
    state = {
      ...state,
      ...touchCache(state, left.channelId, {
        items: saved,
        hasMore: left.hasMore,
        nextCursor: left.nextCursor,
        nextCursorId: left.nextCursorId,
        // Not simply "now": a timeline left open through an outage is as stale as the outage.
        cachedAt: openTimelineFreshAt(prev),
      }),
    }
    if (prev.activeChannel?.id === left.channelId) {
      state = {
        ...state,
        channelDetailCache: {
          ...state.channelDetailCache,
          [left.channelId]: { ...prev.activeChannel, isCached: true },
        },
      }
    }
  }

  const channelId = state.activeChannelId
  if (!channelId) return state
  const { timeline, outbox } = openCachedTimeline(state, channelId, state.messages)
  const cachedDetail: IChannelDetail | undefined = state.channelDetailCache[channelId]
  return {
    ...state,
    outbox,
    activeChannel: state.activeChannel ?? cachedDetail ?? null,
    messages: timeline,
  }
}

/**
 * A conversation's timeline as it opens into `base`: from the cache when there is a copy,
 * with its unsent messages folded in either way. Returns the outbox the fold leaves.
 */
export const openCachedTimeline = (
  state: IChannelsState,
  channelId: string,
  base: IChannelTimeline,
): { timeline: IChannelTimeline; outbox: Record<string, IOutboxEntry> } => {
  const cached = state.messageCache[channelId]
  const { items, outbox } = reconcileWithOutbox(state, channelId, cached?.items ?? [])
  const timeline: IChannelTimeline = {
    ...base,
    items,
    ...(cached
      ? {
          hasMore: cached.hasMore,
          nextCursor: cached.nextCursor,
          nextCursorId: cached.nextCursorId,
          isLoading: false,
          isFromCache: true,
          cachedAt: cached.cachedAt,
        }
      : { isFromCache: false, cachedAt: null }),
  }
  return { timeline, outbox }
}

/** Opened from a recent enough copy that the newest page only needs merging in, not replacing. */
export const isFreshCachedTimeline = (timeline: IChannelTimeline): boolean =>
  !!timeline.isFromCache && Date.now() - (timeline.cachedAt ?? 0) < CACHE_MERGE_WINDOW_MS

/** No answer at all, or a gateway/maintenance one: worth resending on its own, since the id makes it safe. */
const isTransientFailure = (action: OfflineAction) => {
  const status = action.error?.status
  return status === undefined || status >= 502
}

const applySendLifecycle = (
  prev: IChannelsState,
  next: IChannelsState,
  action: OfflineAction,
): IChannelsState => {
  const clientMessageId = action.params?.clientMessageId as string | undefined
  if (!clientMessageId) return next
  const tempId = actions.tempIdForClientMessage(clientMessageId)

  if (action.type === actions.SEND_MESSAGE_REQUEST) {
    const channelId = action.params?.channelId as string
    const data = (action.params?.data ?? {}) as IOutboxSendData
    const existing = prev.outbox[clientMessageId]
    const entry: IOutboxEntry = {
      clientMessageId,
      channelId,
      data,
      createdAt: existing?.createdAt ?? new Date().toISOString(),
      status: 'sending',
    }
    // A retry: the core reducer appended a second copy; keep the original row, now pending.
    const wasShown = prev.messages.items.some((item) => item.id === tempId)
    const items = wasShown
      ? prev.messages.items.map((item) =>
          item.id === tempId
            ? {
                ...item,
                isPending: true,
                isFailed: false,
                isQueued: false,
                failedReason: undefined,
                unsentData: data,
              }
            : item,
        )
      : next.messages.items.map((item) =>
          item.id === tempId ? { ...item, clientMessageId, unsentData: data } : item,
        )
    return {
      ...next,
      outbox: { ...next.outbox, [clientMessageId]: entry },
      messages: { ...next.messages, items },
    }
  }

  if (action.type === actions.SEND_MESSAGE_SUCCESS) {
    return { ...next, outbox: omitKeys(next.outbox, [clientMessageId]) }
  }

  if (action.type === actions.SEND_MESSAGE_FAILURE) {
    const entry = next.outbox[clientMessageId]
    if (!entry) return next
    const isQueued = isTransientFailure(action)
    const failed: IOutboxEntry = isQueued
      ? { ...entry, status: 'queued', failedReason: undefined }
      : { ...entry, status: 'failed', failedReason: action.error?.message }
    return {
      ...next,
      outbox: { ...next.outbox, [clientMessageId]: failed },
      messages: {
        ...next.messages,
        items: next.messages.items.map((item) =>
          item.id === tempId ? rowFromOutboxEntry(next, failed) : item,
        ),
      },
    }
  }
  return next
}

const withTimeline = (state: IChannelsState, channelId: string | null): IChannelsState => {
  if (!channelId || state.messages.channelId !== channelId) return state
  const { items, outbox } = reconcileWithOutbox(state, channelId, state.messages.items)
  return { ...state, outbox, messages: { ...state.messages, items } }
}

const applyOffline = (
  prev: IChannelsState,
  next: IChannelsState,
  action: OfflineAction,
): IChannelsState => {
  switch (action.type) {
    case actions.SET_ACTIVE_CHANNEL:
      return next === prev ? next : switchTimeline(prev, next)

    case actions.SEND_MESSAGE_REQUEST:
    case actions.SEND_MESSAGE_SUCCESS:
    case actions.SEND_MESSAGE_FAILURE:
      return applySendLifecycle(prev, next, action)

    case actions.LIST_MESSAGES_SUCCESS: {
      const channelId = (action.params?.channelId as string) ?? null
      const state = withTimeline(next, channelId)
      if (!channelId || state.messages.channelId !== channelId) return state
      // A load-more reaches back into history; only the newest page says the tail is current.
      const isNewestPage = !action.params?.before
      return {
        ...state,
        messages: {
          ...state.messages,
          isFromCache: false,
          cachedAt: isNewestPage ? Date.now() : state.messages.cachedAt,
        },
      }
    }

    case actions.DISCARD_UNSENT_MESSAGE: {
      const { clientMessageId } = action.data as { clientMessageId: string }
      const tempId = actions.tempIdForClientMessage(clientMessageId)
      return {
        ...next,
        outbox: omitKeys(next.outbox, [clientMessageId]),
        messages: {
          ...next.messages,
          items: next.messages.items.filter((item) => item.id !== tempId),
        },
      }
    }

    case actions.HYDRATE_OUTBOX: {
      // Anything mid-flight when the app last closed never heard back: it waits to resend.
      const restored = Object.fromEntries(
        Object.values(action.data as Record<string, IOutboxEntry>).map((entry) => [
          entry.clientMessageId,
          entry.status === 'sending' ? { ...entry, status: 'queued' as const } : entry,
        ]),
      )
      return withTimeline(
        { ...next, outbox: { ...restored, ...next.outbox } },
        next.activeChannelId,
      )
    }

    case SOCKET_STATUS_CHANGED: {
      const { status } = (action.data ?? {}) as { status?: SocketStatus }
      if (status === SocketStatus.Connected) {
        return next.socketConnectedSince === null
          ? { ...next, socketConnectedSince: Date.now() }
          : next
      }
      return next.socketConnectedSince === null ? next : { ...next, socketConnectedSince: null }
    }

    case actions.HYDRATE_CHANNELS_SNAPSHOT: {
      // Fills only what the network hasn't: a fresh list always beats the stored one.
      const snapshot = action.data as Partial<IChannelsState>
      return {
        ...next,
        channels: next.channels.length ? next.channels : (snapshot.channels ?? next.channels),
        // Merged over the defaults: a snapshot from an older build lacks the newer fields.
        recentConversations: next.recentConversations.items.length
          ? next.recentConversations
          : { ...next.recentConversations, ...snapshot.recentConversations },
        conversationGroups: next.conversationGroups.items.length
          ? next.conversationGroups
          : (snapshot.conversationGroups ?? next.conversationGroups),
        messageCache: { ...withOwnRowsOnly(snapshot.messageCache ?? {}), ...next.messageCache },
        messageCacheOrder: next.messageCacheOrder.length
          ? next.messageCacheOrder
          : (snapshot.messageCacheOrder ?? []),
        channelDetailCache: { ...(snapshot.channelDetailCache ?? {}), ...next.channelDetailCache },
      }
    }

    case actions.PREFETCH_MESSAGES_SUCCESS: {
      const channelId = action.params?.channelId as string
      if (!channelId || channelId === next.messages.channelId) return next
      const data = action.data as {
        messages: IChannelMessageView[]
        hasMore: boolean
        nextCursor: string | null
        nextCursorId?: string | null
      }
      return {
        ...next,
        ...touchCache(next, channelId, {
          items: data.messages,
          hasMore: data.hasMore,
          nextCursor: data.nextCursor,
          nextCursorId: data.nextCursorId ?? null,
          cachedAt: Date.now(),
        }),
      }
    }

    case CHANNEL_MESSAGE_CREATED: {
      const { channelId, message } = action.data as {
        channelId: string
        message: IChannelMessageView
      }
      // The socket echo of our own send can beat its HTTP response.
      const state = withTimeline(next, channelId)
      return patchCachedTimeline(state, channelId, (items) => upsertItem(items, message))
    }

    case CHANNEL_MESSAGE_UPDATED: {
      const { message } = action.data as { message: IChannelMessageView }
      return patchCachedTimeline(next, message?.channelId, (items) =>
        items.some((item) => item.id === message.id) ? upsertItem(items, message) : items,
      )
    }

    case CHANNEL_MESSAGE_DELETED: {
      const { channelId, messageId } = action.data as { channelId?: string; messageId: string }
      return patchCachedTimeline(next, channelId, (items) =>
        items.filter((item) => item.id !== messageId),
      )
    }

    default:
      return next
  }
}

export const withOfflineState =
  <S extends IChannelsState, A extends Action>(reducer: (state: S | undefined, action: A) => S) =>
  (state: S | undefined, action: A): S => {
    const next = reducer(state, action)
    if (!state) return next
    return applyOffline(state, next, action) as S
  }
